import React, { useState, useEffect, useCallback } from 'react';
import { Search, Plus, Pencil, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';
import { BRAND } from './ImpactDashboardTab';
import ImpactRoster from './ImpactRoster';

const TYPE_LABELS = {
  training: 'Training', convening: 'Convening', campaign_action: 'Campaign action',
  application: 'Applications', session: 'Session', meeting: 'Meeting',
};

export default function ImpactEvents({ API_URL, getAuthHeader, refreshKey }) {
  const [events, setEvents] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [search, setSearch] = useState('');
  const [programFilter, setProgramFilter] = useState('');
  const [editEvent, setEditEvent] = useState(null);
  const [rosterEvent, setRosterEvent] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', type: 'training', program_key: '', date: '', delivery_mode: '', hours: '' });

  const fetchAll = useCallback(async () => {
    const qs = new URLSearchParams();
    if (search) qs.set('search', search);
    if (programFilter) qs.set('program', programFilter);
    try {
      const [er, pr] = await Promise.all([
        fetch(`${API_URL}/api/impact/events?${qs}`, { headers: getAuthHeader() }),
        fetch(`${API_URL}/api/impact/programs`, { headers: getAuthHeader() }),
      ]);
      if (er.ok) setEvents(await er.json());
      if (pr.ok) setPrograms(await pr.json());
    } catch { /* noop */ }
  }, [API_URL, getAuthHeader, search, programFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll, refreshKey]);

  const progName = (key) => programs.find(p => p.key === key)?.name || (key ? key : 'Needs classification');

  const createEvent = async () => {
    if (!form.title.trim() || !form.program_key) return toast.error('Title and program are required');
    const body = { ...form, hours: form.hours ? parseFloat(form.hours) : null, date: form.date || null, delivery_mode: form.delivery_mode || null };
    const r = await fetch(`${API_URL}/api/impact/events`, {
      method: 'POST', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify(body)
    });
    if (r.ok) { toast.success('Event added'); setShowForm(false); setForm({ title: '', type: 'training', program_key: '', date: '', delivery_mode: '', hours: '' }); fetchAll(); }
    else toast.error('Could not add event');
  };

  const saveEdit = async () => {
    const orig = events.find(e => e.id === editEvent.id) || {};
    const body = {};
    if (editEvent.program_key && editEvent.program_key !== orig.program_key) body.program_key = editEvent.program_key;
    if (editEvent.title !== orig.title) body.title = editEvent.title;
    if ((editEvent.date || null) !== (orig.date || null)) body.date = editEvent.date || null;
    if ((editEvent.delivery_mode || null) !== (orig.delivery_mode || null)) body.delivery_mode = editEvent.delivery_mode || null;
    const newHours = editEvent.hours === '' || editEvent.hours === null || editEvent.hours === undefined ? null : parseFloat(editEvent.hours);
    if (newHours !== (orig.hours ?? null)) body.hours = newHours;
    if (editEvent.type !== orig.type) body.type = editEvent.type;
    if (Object.keys(body).length === 0) { setEditEvent(null); return; }
    const r = await fetch(`${API_URL}/api/impact/events/${editEvent.id}`, {
      method: 'PATCH', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify(body)
    });
    if (r.ok) { toast.success('Event updated'); setEditEvent(null); fetchAll(); }
    else toast.error('Update failed');
  };

  return (
    <div className="space-y-4" data-testid="impact-events">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search trainings & events…"
            data-testid="impact-events-search" className="pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm w-64" />
        </div>
        <select value={programFilter} onChange={e => setProgramFilter(e.target.value)} data-testid="impact-events-program-filter"
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
          <option value="">All programs</option>
          {programs.map(p => <option key={p.key} value={p.key}>{p.name}</option>)}
        </select>
        <button onClick={() => setShowForm(f => !f)} data-testid="impact-add-event-btn"
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white ml-auto" style={{ background: BRAND.teal }}>
          <Plus className="w-4 h-4" /> Add event / activity
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 grid md:grid-cols-2 gap-3" data-testid="impact-event-form">
          <input placeholder="Title *" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            data-testid="impact-event-title-input" className="border border-slate-300 rounded-lg px-3 py-2 text-sm md:col-span-2" />
          <select value={form.program_key} onChange={e => setForm(f => ({ ...f, program_key: e.target.value }))}
            data-testid="impact-event-program-select" className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
            <option value="">Primary program *</option>
            {programs.filter(p => p.status === 'active').map(p => <option key={p.key} value={p.key}>{p.name}</option>)}
          </select>
          <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
            {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          <div className="flex gap-3">
            <select value={form.delivery_mode} onChange={e => setForm(f => ({ ...f, delivery_mode: e.target.value }))} className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white flex-1">
              <option value="">Delivery mode</option><option value="in_person">In person</option><option value="virtual">Virtual</option><option value="hybrid">Hybrid</option>
            </select>
            <input type="number" step="0.5" min="0" placeholder="Hours" value={form.hours} onChange={e => setForm(f => ({ ...f, hours: e.target.value }))}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm w-24" />
          </div>
          <div className="flex gap-2 md:col-span-2">
            <button onClick={createEvent} data-testid="impact-event-save-btn" className="px-4 py-2 rounded-lg text-sm font-semibold text-white" style={{ background: BRAND.navy }}>Add event</button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-500 border border-slate-200">Cancel</button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm" data-testid="impact-events-table">
          <thead>
            <tr className="text-left text-xs uppercase text-slate-500 border-b bg-slate-50">
              <th className="py-3 px-4">Title</th><th className="px-2">Type</th><th className="px-2">Program</th>
              <th className="px-2">Date</th><th className="px-2">Registrations</th><th className="px-2">Attendance</th><th className="px-2">Source</th><th className="px-2"></th>
            </tr>
          </thead>
          <tbody>
            {events.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-slate-400">No events match. Add a manual event or sync records.</td></tr>}
            {events.map(e => (
              <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`impact-event-row-${e.id}`}>
                <td className="py-3 px-4 font-medium" style={{ color: BRAND.navy }}>{e.title}</td>
                <td className="px-2 text-xs">{TYPE_LABELS[e.type] || e.type}</td>
                <td className="px-2 text-xs">
                  {e.program_key ? progName(e.program_key) : <span className="text-amber-600 font-semibold">Needs classification</span>}
                </td>
                <td className="px-2 text-xs">{e.date || <span className="text-slate-400">Ongoing / undated</span>}</td>
                <td className="px-2">{e.counts?.registrations ?? 0}</td>
                <td className="px-2 text-xs">{(e.counts?.recorded ?? 0) === 0 ? <span className="text-slate-400">Not recorded</span> : `${e.counts.attended} attended`}</td>
                <td className="px-2 text-[11px] text-slate-400">{e.manual ? 'Manual' : e.source_system}</td>
                <td className="px-2">
                  <span className="flex gap-0.5">
                    <button onClick={() => setRosterEvent(e)} data-testid={`impact-event-roster-${e.id}`} title="Roster & attendance"
                      className="p-1.5 rounded-lg text-slate-400 hover:bg-teal-50" style={{ color: undefined }}
                      onMouseEnter={ev => ev.currentTarget.style.color = BRAND.teal} onMouseLeave={ev => ev.currentTarget.style.color = ''}>
                      <ClipboardCheck className="w-3.5 h-3.5" /></button>
                    <button onClick={() => setEditEvent({ ...e })} data-testid={`impact-event-edit-${e.id}`}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"><Pencil className="w-3.5 h-3.5" /></button>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rosterEvent && (
        <ImpactRoster API_URL={API_URL} getAuthHeader={getAuthHeader} event={rosterEvent}
          onClose={() => { setRosterEvent(null); fetchAll(); }} />
      )}

      {editEvent && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditEvent(null)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-lg space-y-3" onClick={e => e.stopPropagation()} data-testid="impact-event-edit-modal">
            <h3 className="font-bold" style={{ color: BRAND.navy, fontFamily: 'Roboto, Arial, sans-serif' }}>Edit event</h3>
            <input value={editEvent.title} onChange={e => setEditEvent(ev => ({ ...ev, title: e.target.value }))} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            <select value={editEvent.program_key || ''} onChange={e => setEditEvent(ev => ({ ...ev, program_key: e.target.value }))}
              data-testid="impact-event-edit-program" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="">Select primary program…</option>
              {programs.map(p => <option key={p.key} value={p.key}>{p.name}</option>)}
            </select>
            <div className="flex gap-3">
              <input type="date" value={editEvent.date || ''} onChange={e => setEditEvent(ev => ({ ...ev, date: e.target.value }))} className="border border-slate-300 rounded-lg px-3 py-2 text-sm flex-1" />
              <input type="number" step="0.5" min="0" placeholder="Hours" value={editEvent.hours ?? ''} onChange={e => setEditEvent(ev => ({ ...ev, hours: e.target.value }))} className="border border-slate-300 rounded-lg px-3 py-2 text-sm w-24" />
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={saveEdit} data-testid="impact-event-edit-save" className="px-4 py-2 rounded-lg text-sm font-semibold text-white" style={{ background: BRAND.teal }}>Save changes</button>
              <button onClick={() => setEditEvent(null)} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-500 border border-slate-200">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
